import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import EmptyState from './EmptyState';
import { Box, Flex, Text } from '@chakra-ui/react';
import EmptyBoardMemberReviews from './EmptyBoardMemberReviews';
import { useState } from 'react';
import InviteAdjudicatorsModal from './InviteAdjudicators';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  fetchAdjudicationInviteDetails,
  fetchHasCompletedReviews
} from 'src/api/queries';
import { ISubmissionReviewer } from '@publishing-engine/domain/models';
import ListLoader from 'src/components/ListLoader';

import {
  Badge,
  Button,
  ButtonIcon,
  ButtonText,
  Menu,
  MenuButton,
  MenuList,
  Table
} from '@morressier/alembic';
import {
  assessmentBadges,
  reviewStatusBadges
} from 'src/components/Review/Badges';
import { formatDeadlineDate } from 'src/utils/date';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/pro-light-svg-icons';
import { IAdjudicationListItem } from '@publishing-engine/domain/views/adjudication';
import ViewAssessmentModal from './ViewAssessmentModal';
import { ISubmissionWFState } from '@publishing-engine/domain/types/workflow-phases';
import SubmittedAuthorDetailsModal from '../AuthorsTab/AuthorProfileModal/SubmittedAuthorDetailsModal';
import { renderMenuItem } from '../ReviewTab/TableColumns';
import { getReviewerNameOrEmail } from 'src/utils/reviews';
import {
  checkOverdueDeadlineMessage,
  MAX_DAYS_TO_EXPIRE_DEADLINE
} from 'src/utils/deadlines';
import ProtectedWorkflowAction from '../../ProtectedAction/ProtectedWorkflowAction';
import ComposeEmailModal, {
  AdHocModalOpen
} from '../AuthorsTab/ComposeEmailModal';
import { SubmissionDetailReviewTab } from '.';
import { TABLE_PAGE_LIMIT } from '../../../constants';
export const adjudicationAllowedStates = [
  'peerReviewPending',
  'peerReviewInvitation',
  'peerReviewFinished',
  'revisionAssessment',
  'revisionPeerReviewInvitation',
  'revisionPeerReviewPending',
  'revisionPeerReviewFinished'
];

interface Props {
  setReviewsSubTab: (tab: SubmissionDetailReviewTab) => void;
}

const AdjudicationsTab: React.FC<Props> = ({ setReviewsSubTab }) => {
  const [isInviteAdjudicatorsModalOpen, setIsInviteAdjudicatorsModalOpen] =
    useState(false);
  const {
    submissionId,
    submissionContextId,
    workflowResponse,
    isTaskCompleter
  } = useSubmissionData();

  const submissionCurrentState = workflowResponse.workflowData
    .currentState as ISubmissionWFState;

  const initialAssessmentStatus =
    submissionCurrentState === 'initialAssessment';

  const revisionStatus = submissionCurrentState === 'revision';

  const isAdjudicationAllowed = adjudicationAllowedStates.includes(
    submissionCurrentState
  );

  const isRevisionAssessmentStatus = [
    'revisionAssessment',
    'revisionPeerReviewInvitation',
    'revisionPeerReviewPending',
    'revisionPeerReviewFinished'
  ].includes(submissionCurrentState);

  const submissionState = workflowResponse.workflowData.currentState;

  const { data: hasCompletedReviews } = useQuery({
    queryKey: [
      'has-completed-reviews',
      {
        submissionId,
        submissionState
      }
    ],
    queryFn: () => fetchHasCompletedReviews(submissionId, submissionState)
  });

  const [selectedExpert, setSelectedExpert] =
    useState<ISubmissionReviewer | null>(null);

  const [selectedViewAdjudication, setSelectedViewAdjudication] =
    useState<IAdjudicationListItem | null>(null);
  const [openSendEmailModal, setOpenSendEmailModal] = useState<AdHocModalOpen>({
    status: false,
    defaultEmail: []
  });

  const {
    data: result,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: [
      'adjudications-experts-invite-list',
      { submissionContextId, submissionId }
    ],
    queryFn: ({ pageParam = 0 }) =>
      submissionId && submissionContextId
        ? fetchAdjudicationInviteDetails(
            submissionContextId,
            submissionId,
            'adjudications',
            pageParam,
            TABLE_PAGE_LIMIT
          )
        : null,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) return undefined;
      const maxOffset = lastPage.total;
      const nextOffset = allPages.length * TABLE_PAGE_LIMIT;
      return nextOffset < maxOffset ? nextOffset : undefined;
    }
  });

  const {
    total = 0,
    completedAssessment = 0,
    declinedOrExpired = 0,
    inProgress = 0
  } = result?.pages?.[0] || {};

  const items = result?.pages?.flatMap(page => page?.items || []) || [];

  const showCompletedAdjudications = items.filter(
    item => item.workflow.currentState === 'reviewed'
  );

  const renderLoadMore = () => {
    if (!hasNextPage) return null;
    return (
      <Flex justifyContent="center" marginTop={4}>
        <Button
          isDisabled={isFetchingNextPage}
          variant="secondary"
          onClick={() => fetchNextPage()}
          isLoading={isFetchingNextPage}
        >
          Load More
        </Button>
      </Flex>
    );
  };

  const handleViewPeerReview = () => {
    setReviewsSubTab('Peer Reviews');
    setTimeout(() => {
      const element = document.querySelector('#review-history');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  if (isLoading) {
    return <ListLoader />;
  }

  return (
    <>
      {isInviteAdjudicatorsModalOpen && (
        <InviteAdjudicatorsModal
          closeModal={() => setIsInviteAdjudicatorsModalOpen(false)}
        />
      )}
      {selectedExpert?.id ? (
        <SubmittedAuthorDetailsModal
          backButtonText="Back to Reviews"
          userId={selectedExpert?.id ?? ''}
          onBackClick={() => setSelectedExpert(null)}
        />
      ) : null}
      {selectedViewAdjudication ? (
        <ViewAssessmentModal
          assessmentData={selectedViewAdjudication}
          onClose={() => setSelectedViewAdjudication(null)}
          type="Adjudication"
        />
      ) : null}
      <Flex flexDirection="column" marginTop={6}>
        {revisionStatus ? (
          <EmptyState
            title="Revision Undergoing"
            subTitle={
              <span>
                The author has been invited to revise their work. During this
                time, you can only access reviews and adjudications from
                previous rounds in the{' '}
                <ButtonText onClick={handleViewPeerReview}>
                  Peer Review
                </ButtonText>{' '}
                section.
              </span>
            }
            headingText="Adjudications"
            subHeadingText="This work is only active in Revision Assessment phase."
          />
        ) : initialAssessmentStatus ? (
          <EmptyState
            title="Adjudications Available After Peer Review"
            subTitle="This step is part of the Peer Review phase: in this phase, you will be able to invite Experts to assess this Submission’s Reviews, act as a referee, and help inform your decision."
            headingText="Adjudications"
          />
        ) : isAdjudicationAllowed && !hasCompletedReviews ? (
          <EmptyState
            title="Adjudications Require Completed Reviews"
            subTitle={
              <span>
                No completed reviews available in this round. Adjudications can
                be initiated after obtaining completed Reviews, where you will
                be able to invite experts to referee.{' '}
                {isRevisionAssessmentStatus && (
                  <span>
                    Reviews and adjudications from previous rounds can be
                    accessed in the{' '}
                    <ButtonText onClick={handleViewPeerReview}>
                      Peer Review
                    </ButtonText>{' '}
                    section.
                  </span>
                )}
              </span>
            }
            headingText="Adjudications"
          />
        ) : isAdjudicationAllowed && hasCompletedReviews ? (
          <>
            {openSendEmailModal.status && (
              <ComposeEmailModal
                setOpenSendEmailModal={setOpenSendEmailModal}
                email={openSendEmailModal.defaultEmail}
              />
            )}
            <Flex gap={2} flexDirection={'column'}>
              <Text textStyle="headingSection">Adjudications</Text>
              <p>
                {total} Adjudicators Invited: {completedAssessment}{' '}
                Adjudications Completed, {inProgress} In Progress,{' '}
                {declinedOrExpired} Declined/Expired
              </p>
              {isAdjudicationAllowed && hasCompletedReviews && total !== 0 && (
                <Box marginTop={4}>
                  <ProtectedWorkflowAction actionName="RequestAdjudication">
                    <Button
                      onClick={() =>
                        setIsInviteAdjudicatorsModalOpen(prev => !prev)
                      }
                    >
                      Invite Adjudicators
                    </Button>
                  </ProtectedWorkflowAction>
                </Box>
              )}
            </Flex>
            {result && total === 0 ? (
              <EmptyBoardMemberReviews
                onInviteClick={() =>
                  setIsInviteAdjudicatorsModalOpen(prev => !prev)
                }
                title={`No Adjudicators Yet Invited ${
                  isRevisionAssessmentStatus ? 'In This Round' : ''
                }`}
                subtext={
                  <span>
                    Invite Adjudicators to assess this Submission’s Reviews, act
                    as a referees, and help inform your decision.
                    {isRevisionAssessmentStatus && (
                      <span>
                        {' '}
                        Reviews and adjudications from previous rounds can be
                        accessed in the{' '}
                        <ButtonText onClick={handleViewPeerReview}>
                          Peer Review
                        </ButtonText>{' '}
                        section.
                      </span>
                    )}
                  </span>
                }
                buttonText="Invite Adjudicators"
                renderInviteCallToAction
                workflowAction="RequestAdjudication"
              />
            ) : (
              <Box width="100%" marginTop={8} overflowX="auto">
              <Box minWidth="520px">
                <Table>
                  <Table.HeaderRow padding={2}>
                    <Table.HeaderCell flex="2">Adjudicator</Table.HeaderCell>
                    <Table.HeaderCell flex="1">Status</Table.HeaderCell>
                    <Table.HeaderCell flex="1">Deadline</Table.HeaderCell>
                    <Table.HeaderCell flex="1">Recommendation</Table.HeaderCell>
                    <Table.HeaderCell width="44px"></Table.HeaderCell>
                  </Table.HeaderRow>
                  <Table.Body sx={{ borderTop: 'none' }}>
                    {items.map((item, index) => {
                      const deadlineDate = item.workflow.currentDeadline
                        ? new Date(item.workflow.currentDeadline)
                        : null;
                      const { deadlineMessage, todaysDate, daysToDeadline } =
                        checkOverdueDeadlineMessage(
                          deadlineDate,
                          item.workflow.currentState
                        );
                      return (
                        <>
                          <Table.Row
                            key={index}
                            style={{
                              cursor:
                                item.workflow.currentState === 'reviewed' &&
                                !!item?.caseData?.adjudication
                                  ? 'pointer'
                                  : 'default'
                            }}
                            onClick={() =>
                              item.workflow.currentState === 'reviewed' &&
                              !!item?.caseData?.adjudication &&
                              setSelectedViewAdjudication(item ?? null)
                            }
                          >
                            <Table.RowCell flex="2" textAlign="left">
                              {item?.expert ? (
                                <Flex flexDirection="column">
                                  <Text textStyle="headingItem">
                                    {getReviewerNameOrEmail(item?.expert)}
                                  </Text>
                                  <Text textStyle="textContext">
                                    {
                                      item?.expert?.affiliationDetails
                                        ?.organizationName
                                    }
                                  </Text>
                                  {item?.expert?.role &&
                                    item?.expert?.role === 'board-member' && (
                                      <Box marginTop={2}>
                                        <Badge
                                          variant="attention"
                                          size="context"
                                        >
                                          Board Member
                                        </Badge>
                                      </Box>
                                    )}
                                </Flex>
                              ) : (
                                'N/A'
                              )}
                            </Table.RowCell>
                            <Table.RowCell flex="1">
                              {reviewStatusBadges[item.workflow.currentState] ||
                                reviewStatusBadges.firstAssessment}
                            </Table.RowCell>
                            <Table.RowCell flex="1">
                              {item.workflow?.currentDeadline !== null &&
                              item.workflow?.currentDeadline ? (
                                <Flex flexDirection="column">
                                  <Text
                                    textStyle="headingItem"
                                    color={
                                      (deadlineDate &&
                                        todaysDate >= deadlineDate) ||
                                      daysToDeadline <=
                                        MAX_DAYS_TO_EXPIRE_DEADLINE
                                        ? 'actionSecondaryCautionContent'
                                        : ''
                                    }
                                  >
                                    {formatDeadlineDate(
                                      new Date(item.workflow?.currentDeadline)
                                    )}
                                  </Text>
                                  {item.workflow?.currentDeadline && (
                                    <Text>{deadlineMessage}</Text>
                                  )}
                                </Flex>
                              ) : (
                                'N/A'
                              )}
                            </Table.RowCell>
                            <Table.RowCell flex="1">
                              {item?.caseData?.adjudication
                                ? assessmentBadges[
                                    item.caseData.adjudication?.recommendation
                                  ]
                                : 'N/A'}
                            </Table.RowCell>
                            <Table.RowCell
                              padding={2}
                              width="44px"
                              key="actions"
                            >
                              <div style={{ position: 'relative' }}>
                                <Menu ariaLabel="Actions">
                                  <MenuButton
                                    as={ButtonIcon}
                                    onClick={event => {
                                      event.stopPropagation();
                                    }}
                                    icon={<FontAwesomeIcon icon={faEllipsis} />}
                                    color="transparent"
                                  />
                                  <MenuList
                                    style={{
                                      position: 'absolute',
                                      zIndex: 1000,
                                      top: '48px'
                                    }}
                                  >
                                    {item.workflow.currentState ===
                                      'reviewed' &&
                                      !!item?.caseData?.adjudication &&
                                      renderMenuItem('View Adjudication', () =>
                                        setSelectedViewAdjudication(
                                          item ?? null
                                        )
                                      )}
                                    {renderMenuItem('View Expert Profile', () =>
                                      setSelectedExpert(item.expert)
                                    )}

                                    {isTaskCompleter &&
                                      renderMenuItem('Send Email', () =>
                                        setOpenSendEmailModal({
                                          status: true,
                                          defaultEmail: [item.expert.email]
                                        })
                                      )}
                                  </MenuList>
                                </Menu>
                              </div>
                            </Table.RowCell>
                          </Table.Row>
                        </>
                      );
                    })}
                  </Table.Body>
                </Table>
                {renderLoadMore()}
              </Box>
            </Box>
            )}
          </>
        ) : (
          <Box marginTop={6}>
            {result &&
            items?.some(item => item.workflow.currentState === 'reviewed') ? (
              <>
                <Flex flexDirection="column" marginBottom={6}>
                  <Text textStyle="headingSection">Adjudications</Text>
                  <Text>
                    This work is only active in Peer Review. Our system carried
                    over the following completed work:
                  </Text>
                </Flex>
                {showCompletedAdjudications?.map(item => (
                  <Flex
                    justifyContent="space-between"
                    alignItems={{ base: 'flex-start', md: 'center' }}
                    flexDirection={{ base: 'column', md: 'row' }}
                    gap={2}
                    padding={4}
                    bgColor="surfacePrimaryFill"
                    borderColor="neutralPrimaryBorder"
                    width="100%"
                    marginY={3}
                  >
                    <Flex flexDirection="column">
                      <Text textStyle="headingItem">
                        {item.expert.firstName} {item.expert.lastName}
                      </Text>
                      <Text>
                        {item.expert.affiliationDetails?.organizationName}
                      </Text>
                    </Flex>
                    <ButtonText
                      onClick={() => setSelectedViewAdjudication(item ?? null)}
                    >
                      View Adjudication
                    </ButtonText>
                  </Flex>
                ))}
              </>
            ) : (
              <EmptyState
                title="No Adjudications"
                subTitle="No adjudications were carried over from either the Peer Review or Revision Assessment phases."
                headingText="Adjudications"
                subHeadingText="This work is only active in Peer Review."
                isFileIcon
              />
            )}
          </Box>
        )}
      </Flex>
    </>
  );
};
export default AdjudicationsTab;
